import { CircuitBreaker } from '../utils/circuitBreaker';
import { getGlobalAbortSignal } from '../utils/abortController';

// Define API_URL constant
const API_URL = 'https://api.srcful.dev/';

// Battery data types based on actual API response
export interface BatteryLatest {
  ts: string;
  power: number;  // Current power (positive = charging, negative = discharging)
  soc: number;    // State of charge percentage
  current: number;
  voltage: number;
}

export interface BatteryHistorical {
  ts: string;
  power: number;
  soc: number;
}

export interface BatteryData {
  latest: BatteryLatest;
  historical: BatteryHistorical[];
}

/**
 * Service for fetching real battery data from the API
 */
class BatteryDataService {
  private static instance: BatteryDataService;
  private circuitBreaker: CircuitBreaker;

  private constructor() {
    // Initialize circuit breaker with 3 max failures and 30s reset timeout
    this.circuitBreaker = new CircuitBreaker(3, 30000);
  }

  public static getInstance(): BatteryDataService {
    if (!BatteryDataService.instance) {
      BatteryDataService.instance = new BatteryDataService();
    }
    return BatteryDataService.instance;
  }

  /**
   * Execute a GraphQL query and handle errors
   * @param query - GraphQL query string
   * @returns Parsed response data
   */
  private async executeGraphQLQuery(query: string): Promise<any> {
    // Use circuit breaker to prevent continuous failures
    return this.circuitBreaker.execute(async () => {
      try {
        const requestHeaders: HeadersInit = new Headers();
        requestHeaders.set('Content-Type', 'application/json');
        requestHeaders.set('Content-Length', query.length.toString());

        // Use the global abort signal to ensure this request can be cancelled
        const response = await fetch(API_URL, {
          method: 'POST',
          body: query,
          headers: requestHeaders,
          signal: getGlobalAbortSignal()
        });

        const json = await response.json();
        if (json.errors) {
          console.error('GraphQL query error:', json.errors);
          throw new Error(json.errors[0]?.message || 'Unknown GraphQL error');
        }

        return json.data;
      } catch (err) {
        // Don't log or propagate abort errors
        if (err instanceof DOMException && err.name === 'AbortError') {
          console.log('Request aborted by user action');
          throw new Error('Request cancelled');
        }
        
        console.error('GraphQL query error:', err);
        throw err;
      }
    });
  }

  /**
   * Check if circuit breaker is currently open (refusing requests)
   * @returns boolean indicating if circuit is open
   */
  public isCircuitOpen(): boolean {
    return this.circuitBreaker.isCircuitOpen();
  }

  /**
   * Fetch battery data for a specific serial number
   * @param serialNumber - Battery serial number
   * @param startDate - Start date for historical data
   * @param stopDate - Stop date for historical data  
   * @param resolution - Data resolution (e.g., "24h", "1h", "5m")
   * @returns Promise with battery data
   */
  public async getBatteryData(
    serialNumber: string,
    startDate?: string,
    stopDate?: string,
    resolution: string = "24h"
  ): Promise<BatteryData | null> {
    if (!serialNumber) return null;
    
    try {
      // Check if circuit breaker is open
      if (this.circuitBreaker.isCircuitOpen()) {
        console.warn('Circuit breaker open - skipping battery data request');
        return null;
      }
      
      // Set default date range if not provided (last 30 days)
      const defaultEndDate = new Date().toISOString();
      const defaultStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      const start = startDate || defaultStartDate;
      const stop = stopDate || defaultEndDate;
      
      const query = JSON.stringify({
        query: `
          query battery_data {
            derData {
              battery(sn:"${serialNumber}") {
                latest {
                  ts
                  power
                  soc
                  current
                  voltage
                }
                historical(start:"${start}", stop:"${stop}", resolution: "${resolution}") {
                  ts
                  power
                  soc
                }
              }
            }
          }
        `
      });

      const data = await this.executeGraphQLQuery(query);
      
      // Transform the data to match our interface
      const batteryData = data?.derData?.battery;
      
      if (!batteryData) {
        return null;
      }
      
      return {
        latest: {
          ts: batteryData.latest?.ts || '',
          power: batteryData.latest?.power || 0,
          soc: batteryData.latest?.soc || 0,
          current: batteryData.latest?.current || 0,
          voltage: batteryData.latest?.voltage || 0
        },
        historical: (batteryData.historical || []).map((item: any) => ({
          ts: item.ts,
          power: item.power || 0,
          soc: item.soc || 0
        }))
      };
    } catch (err) {
      console.error(`Error fetching battery data for serial number ${serialNumber}:`, err);
      return null;
    }
  }

  /**
   * Fetch intraday battery data with higher resolution
   * @param serialNumber - Battery serial number
   * @param startDate - Start date for the day
   * @param stopDate - Stop date for the day
   * @param resolution - Data resolution (e.g., "5m", "15m", "1h")
   * @returns Promise with intraday battery data
   */
  public async getIntradayBatteryData(
    serialNumber: string,
    startDate: string,
    stopDate: string,
    resolution: string = "15m"
  ): Promise<BatteryHistorical[]> {
    if (!serialNumber) return [];
    
    try {
      if (this.circuitBreaker.isCircuitOpen()) {
        console.warn('Circuit breaker open - skipping intraday battery data request');
        return [];
      }
      
      const query = JSON.stringify({
        query: `
          query intraday_battery_data {
            derData {
              battery(sn:"${serialNumber}") {
                historical(start:"${startDate}", stop:"${stopDate}", resolution: "${resolution}") {
                  ts
                  power
                  soc
                }
              }
            }
          }
        `
      });

      const data = await this.executeGraphQLQuery(query);
      const batteryData = data?.derData?.battery;
      
      return (batteryData?.historical || []).map((item: any) => ({
        ts: item.ts,
        power: item.power || 0,
        soc: item.soc || 0
      }));
    } catch (err) {
      console.error(`Error fetching intraday battery data for serial number ${serialNumber}:`, err);
      return [];
    }
  }
}

const batteryDataService = BatteryDataService.getInstance();
export default batteryDataService; 